import { EditType } from 'src/types/edit';
import { InsightSummaryPage } from 'src/types/pages/insights';

export const TRAINING_DEVELOPMENT: InsightSummaryPage = {
  page_id: 'insights-14',
  section_order: ['header', 'summary'],
  header: {
    visible: true,
    content: {
      title: 'Training + Development',
      body: 'Mediators express great interest in skills training and the continued cultivation of their practice. Participants share their thoughts on professional development.',
      header_type: 'full',
    },
  },
  summary: {
    visible: true,
    content: {
      highlight_link_style: {
        default_color: '#11304B',
        active_color: '#48bf53',
      },
      paragraphs: [
        {
          label: 'Overview & Recommendations',
          text: 'The onset of the pandemic and the closure of the nation necessitated an abrupt shift in the FMCS approach toward training and development. The use of remote meeting technologies and remote meeting training and service delivery tools were introduced and employed at a rapid pace. The style of training had to change as well, as techniques which proved to be effective for in-person meetings did not translate well into the virtual space. There were many growing pains in this season of transition, for both the learners and facilitators, which led to <Embed getEmbedInfo={getEmbedInfo}>opportunities for growth-5249123</Embed>, innovation and exposed some blind spots.<br/><br/>The agency continues to be <Embed getEmbedInfo={getEmbedInfo}>nimble and innovative-5249096</Embed> regarding training & development; critically examining and updating the internal & external approaches toward the timing, delivery, and frequency of training & development sessions.<br/><br/>Recommendations from conversations include:<br/><ul><li>The agency continues to refine the training & development system regarding the preparation & support of FMCS service providers meeting and exceeding the evolving demands of the business environment and <Embed getEmbedInfo={getEmbedInfo}>needs of the clients-5248129</Embed></li><li>The agency continuously reviews training content as well as the suite of training classes offered internally and to external customers to make sure the <Embed getEmbedInfo={getEmbedInfo}>sessions are meaningful-5250340</Embed> and valuable to the participants as they look to apply the knowledge gained in the training experience in performance of their duties.</li><li>The agency emphasizes upskilling as a vital element for all field mediators and support staff performance standards, and holds FMCS members accountable to the <Embed getEmbedInfo={getEmbedInfo}>culture of continuous improvement-5249114</Embed> in execution of their duties.</li><li>FMCS charts and clearly communicates the skills needed and the importance of demonstrated mastery of the <Embed getEmbedInfo={getEmbedInfo}>minimum skill requirements-5250594</Embed> for employment consideration for all positions with the agency.</li></ul>',
        },
        {
          label: 'Upskilling',
          text: "Prior to the onset of pandemic restrictions, the agency had undergone some departmental reorganization, so in the observation of <Embed getEmbedInfo={getEmbedInfo}>Speaker CC-5249053</Embed>, these changes aligned with the opportunity to commit to new training and development: “We not only went into pandemic lockdown, we had a reorganized agency, like immediately before it, and then we went into lockdown. So for me personally, I had a new role, a new structure in the organization…” and for Speaker CC, they saw the opportunity as a re-commitment to their love of being a mediator.<br/><br/>Participants in their conversations shared their observations on best practices for upskilling. <Embed getEmbedInfo={getEmbedInfo}>Speaker TA-5248969</Embed> suggested keeping virtual training short due to the tiring nature of online training and as <Embed getEmbedInfo={getEmbedInfo}>Speaker SC-5249068</Embed> offered in observation, to include some element of fun. <Embed getEmbedInfo={getEmbedInfo}>Speaker LB-5249049</Embed> noted that new mediator training can sometimes be too “academic” and then suggested that perhaps current mediators, “...share some of their stories and give some insight to the mediators of what they're about to embark on.” <br/><br/><Embed getEmbedInfo={getEmbedInfo}>Speaker T,-5248973</Embed>, relayed their experience of evolving federal training programs and pointed out the differences between the traditional skills training they’ve received and their hopes for future professional development, “I come from a federal background. I was a federal HR manager for years and years. One of the biggest changes for me, and it was a kind of epiphany of realizing that I spent years and years doing training for the federal government, that was about checking boxes, about making sure that I could document that I had given people the information that I was required to make sure they had, rather than doing training to teach people something.” <br/><br/>Overall, there is great enthusiasm for any form of professional development, particularly in the realm of technology and advanced virtual platforms. <Embed getEmbedInfo={getEmbedInfo}>Speaker TC-5248122</Embed> I have found it liberating. I have tried to master Zoom and MS Teams and other technology, but it's enabled me with some mastery of breakout rooms to be very effective in my mediation techniques.",
        },
        {
          label: 'Resistant to Change',
          text: "Participants, even as they express trepidation about mastering new technologies to perform virtual negotiations, there is also what <Embed getEmbedInfo={getEmbedInfo}>Speaker AB-5249105</Embed> refers to as the will to, “...resist my inner dinosaur.” Some of the uneasiness regarding technology stems from being inexperienced, with some mediators having very little experience with online workspaces and video-conferencing platforms. <Embed getEmbedInfo={getEmbedInfo}>Speaker AB-5249105</Embed> further adds that prior to pandemic lockdowns, they had performed only one virtual meeting: “That was my only virtual world experience. I had never FaceTimed, nothing. I don't even know what the names of the other stuff beyond what we use are, still, okay?” <br/><br/>Some mediators brought a long history of technology avoidance  like, <Embed getEmbedInfo={getEmbedInfo}>Speaker TA- 5250399</Embed> explained that they, “...fought telework in my previous position for years…I wanted nothing to do with telework. I like being around people. I did not want to work from home. Fought it, fought it, fought it. So when this came out, it was quite a shock to my system. I didn't fight it because I knew we had to do it, but I just really was not looking forward to it.” Others, like <Embed getEmbedInfo={getEmbedInfo}>Speaker WB-5249101</Embed> expressed a practical approach in that going virtual allowed everyone to “continue working” and that mediators should work with clients to get them increasingly comfortable with virtual negotiations.",
        },
        {
          label: 'Technology',
          text: "The majority of mediators, even those who had relatively little inclination or experience with remote work, quickly adjusted to new platforms with an eagerness to learn more. One participant, <Embed getEmbedInfo={getEmbedInfo}>Speaker AB-5249338</Embed> who began their career with relatively little technological experience, shares their light-hearted story of quickly getting up to speed: “So my husband came home one day in July of 2020, and he's like, “Hey, Switch.” He called me Switch. And it took me a minute to realize he was calling me Switch because that's one of the characters in The Matrix…I had one meeting on my personal laptop, one on my work laptop, and another meeting on my phone, at the same time…I was like, “Holy crap, seven months ago, I could barely manage email on my phone!”<br/><br/>Several mediators like <Embed getEmbedInfo={getEmbedInfo}>Speaker BA-5250344</Embed>, <Embed getEmbedInfo={getEmbedInfo}>Speaker CD-5250617</Embed> and <Embed getEmbedInfo={getEmbedInfo}>Speaker EA-5248122</Embed> express enthusiasm to embrace remote and virtual platforms. Speaker CD shares their experiences as, “Oh, I'm so ready for this.” <Embed getEmbedInfo={getEmbedInfo}>Speaker TC-5250893</Embed> summarizes what fellow participants feel as an opportunity to collect new tools in their mediator’s toolbox: “Learning a new skill set is really exciting to me. It's a bit of a challenge. I'm looking like I'm going to have to stretch and grow again, which fires me up.” And one senior mediator, <Embed getEmbedInfo={getEmbedInfo}>Speaker JB-5249117</Embed>, places the role of new technology in context of their long professional career, “The opportunity, now I'm two plus decades into a career as a conflict manager professional, and I have never seen the sheer volume of access to high quality meetings to sort out problems that these platforms provide. It's unbelievable…They're helping people they never would have been able to help before.”",
        },
      ],
    },
  },
  edit_metadata: {
    page_label: 'Training + Development',
    page_id: 'insights-14',
    subroute: true,
    sections: [
      {
        section_label: 'Introduction',
        edit_blocks: [
          {
            block_label: 'Header',
            match: 'header.content',
            type: EditType.banner,
          },
        ],
      },
      {
        section_label: 'Highlight Link Colors',
        edit_blocks: [
          {
            block_label: 'Color',
            match: 'summary.content.highlight_link_style.default_color',
            type: EditType.color,
          },
          {
            block_label: 'Active Color',
            match: 'summary.content.highlight_link_style.active_color',
            type: EditType.color,
          },
        ],
      },
      {
        section_label: 'Paragraphs',
        update_section: {
          match: 'summary.content.paragraphs',
          label: 'Add Paragraph',
          empty: {
            label: 'Default Label',
            text: 'Default Text',
          },
          newSectionMetadata: {
            section_label: 'Insight',
            numbered: true,
            edit_blocks: [
              {
                block_label: 'Title',
                match: `summary.content.paragraphs[index].label`,
                type: EditType.string,
              },
              {
                block_label: 'Paragraph',
                match: `summary.content.paragraphs[index].text`,
                type: EditType.embed_string,
              },
            ],
          },
        },
        sub_sections: [
          {
            section_label: 'Overview',
            edit_blocks: [
              {
                block_label: 'Title',
                match: 'summary.content.paragraphs[0].label',
                type: EditType.string,
              },
              {
                block_label: 'Paragraph',
                match: 'summary.content.paragraphs[0].text',
                type: EditType.embed_string,
              },
            ],
          },
          {
            section_label: 'Insight',
            numbered: true,
            edit_blocks: [
              {
                block_label: 'Title',
                match: 'summary.content.paragraphs[1].label',
                type: EditType.string,
              },
              {
                block_label: 'Paragraph',
                match: 'summary.content.paragraphs[1].text',
                type: EditType.embed_string,
              },
            ],
          },
          {
            section_label: 'Insight',
            numbered: true,
            edit_blocks: [
              {
                block_label: 'Title',
                match: 'summary.content.paragraphs[2].label',
                type: EditType.string,
              },
              {
                block_label: 'Paragraph',
                match: 'summary.content.paragraphs[2].text',
                type: EditType.embed_string,
              },
            ],
          },
          {
            section_label: 'Insight',
            numbered: true,
            edit_blocks: [
              {
                block_label: 'Title',
                match: 'summary.content.paragraphs[3].label',
                type: EditType.string,
              },
              {
                block_label: 'Paragraph',
                match: 'summary.content.paragraphs[3].text',
                type: EditType.embed_string,
              },
            ],
          },
        ],
      },
    ],
  },
};
